
import { useState } from 'react';

export default function EarningsCalculator() {
  const [hourlyRate, setHourlyRate] = useState(55);
  const [hoursPerWeek, setHoursPerWeek] = useState(30);

  const grossWeekly = hourlyRate * hoursPerWeek;
  const grossMonthly = grossWeekly * 4.33;
  const grossYearly = grossWeekly * 52;

  const netWeekly = grossWeekly * 0.85;
  const netMonthly = grossMonthly * 0.85;
  const netYearly = grossYearly * 0.85;

  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 sm:p-8 mb-8 sm:mb-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Earnings Calculator
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            See your potential earnings based on your hourly rate and hours worked
          </p>
        </div>

        {/* Sliders */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm">
          <div className="space-y-4 sm:space-y-6">
            {/* Hourly Rate Slider */}
            <div>
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <label className="text-sm sm:text-base font-semibold text-gray-700">
                  Hourly Rate
                </label>
                <span className="text-xl sm:text-2xl font-bold text-teal-600">
                  £{hourlyRate}
                </span>
              </div>
              <input
                type="range"
                min="30"
                max="110"
                step="5"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-1">
                <span>£30</span>
                <span>£110</span>
              </div>
            </div>

            {/* Hours Per Week Slider */}
            <div>
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <label className="text-sm sm:text-base font-semibold text-gray-700">
                  Hours Per Week
                </label>
                <span className="text-xl sm:text-2xl font-bold text-teal-600">
                  {hoursPerWeek}h
                </span>
              </div>
              <input
                type="range"
                min="5"
                max="60"
                step="5"
                value={hoursPerWeek}
                onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-1">
                <span>5h</span>
                <span>60h</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gross Earnings */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
            Gross Earnings
          </h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Weekly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{grossWeekly.toLocaleString()}
              </div>
            </div>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Monthly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{Math.round(grossMonthly).toLocaleString()}
              </div>
            </div>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Yearly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{Math.round(grossYearly).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Your Take-Home */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
            Your Take-Home (After 15% Commission)
          </h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Weekly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{Math.round(netWeekly).toLocaleString()}
              </div>
              <div className="text-xs opacity-90 mt-1">
                (85% of £{grossWeekly.toLocaleString()})
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Monthly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{Math.round(netMonthly).toLocaleString()}
              </div>
              <div className="text-xs opacity-90 mt-1">
                (85% of £{Math.round(grossMonthly).toLocaleString()})
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-3 sm:p-4 text-white">
              <div className="text-xs sm:text-sm font-medium mb-1">Yearly</div>
              <div className="text-lg sm:text-2xl font-bold">
                £{Math.round(netYearly).toLocaleString()}
              </div>
              <div className="text-xs opacity-90 mt-1">
                (85% of £{Math.round(grossYearly).toLocaleString()})
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <i className="ri-information-line text-blue-600 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-xs sm:text-sm text-blue-900">
                <strong>Platform Fee:</strong> Taskbudy charges a 15% commission on completed jobs. 
                You keep 85% of all earnings. Payments are processed weekly directly to your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
